import React, {useEffect, useRef, useState} from "react";
import {InfoCircle} from "iconsax-react";
import {Trans, useTranslation} from "react-i18next";
import {Form, useForm} from "react-hook-form";
import {Modal} from "../Modal/Modal";
import {ModalNames} from "../../../Constants/modalNames";
import {Div, HasLoading, Icon, Input, Select, Text} from "../../Kit";
import {Button} from "../../Kit/Button";
import {useLanguage} from "../../../Store/modules/language";
import {ToggleSwitch} from "../ToggleSwitch/ToggleSwitch";
import {useGetUserPersonalization} from "../../../Store/modules/ai/getUserPersonalization/getUserPersonalization";
import {usePostUserPersonalization} from "../../../Store/modules/ai/postUserPersonalization/postUserPersonalization";
import {
    useSeenAnnouncedNotification
} from "../../../Store/modules/ai/postSeenAnnouncedNotification/postSeenAnnouncedNotification";
import {useTheme, useThemeColors} from "../../../Store/modules/theme";
import {useModal} from "../../../Store/modules/modal/modal";
import {resizeTextareaToContent} from "../../../Utils/textareaHandler";
import {getTextDirection} from "../../../Utils/stringProcessor";
import {PuffLoader} from "react-spinners";

export type PersonalizationForm = {
    nickname?: string;
    job?: string;
    about?: string;
    interests?: string;
    generated_images_quality?: { label: string, value: string };
    generated_video_quality?: { label: string, value: string };
    generated_images_format?: { label: string, value: string };
    language?: { label: string, value: string };
    narrator_voice?: { label: string, value: string };
    generated_images_size?: { label: string, value: string };
    generated_images_bg?: { label: string, value: string };
    generated_video_model?: { label: string, value: string };
    generated_video_size?: { label: string, value: string };
    generated_video_time?: { label: string, value: string };
};

export const PersonalizationModal: React.FC = () => {
    const {
        getUserPersonalizationData,
        dispatchGetUserPersonalization,
        getUserPersonalizationLoading
    } = useGetUserPersonalization();
    const {dispatchPostUserPersonalization, postUserPersonalizationLoading} = usePostUserPersonalization();
    const {name: themeStatus} = useTheme();
    const [theme] = useThemeColors();
    const {name, dispatchCloseModal,modalProps} = useModal();
    let {mode = "full"} = modalProps;

    const {t} = useTranslation();

    let themeDark = themeStatus === "dark";
    const {direction} = useLanguage();
    let {data} = getUserPersonalizationData?.data || {};
    useSeenAnnouncedNotification();

    const OPTIONS = [
        "chatty",
        "witty",
        "humble",
        "encouraging",
        "straightShooting",
        "genZ",
        "traditional",
        "forwardThinking",
        "poetic",
        "opinionated",
        "silly",
        "direct",
        "pragmatic",
        "corporate",
        "outsideTheBox",
        "empathetic"
    ];

    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [useHistory, setUseHistory] = useState<boolean>(false);
    const [enableWebSave, setEnableWebSave] = useState<boolean>(false);
    const [useWebSearching, setWebSearching] = useState<boolean>(false);
    const [hasChanged, setHasChanged] = useState(false);

    const toggleOption = (option: string) => {
        setHasChanged(true);
        setSelectedOptions(prev =>
            prev.includes(option)
                ? prev.filter(item => item !== option)
                : [...prev, option]
        );
    };

    useEffect(() => {
        setHasChanged(false);
    }, [name]);

    useEffect(() => {
        if (name === ModalNames.PersonalizationModal) {
            dispatchGetUserPersonalization();
        }
    }, [name]);

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: {isDirty}
    } = useForm<PersonalizationForm>({
        mode: "onSubmit"
    });

    const handleToggleHistory = (value: boolean) => {
        setUseHistory(value);
        setHasChanged(true);
    };

    const handleToggleEnableWebSave = (value: boolean) => {
        setEnableWebSave(value);
        setHasChanged(true);
    };

    const handleToggleMemory = (value: boolean) => {
        setWebSearching(value);
        setHasChanged(true);
    };

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const qualityOptions = [
        // @ts-ignore
        {label: t("personalization.generatedImages.quality.low"), value: "low"},
        // @ts-ignore
        {label: t("personalization.generatedImages.quality.medium"), value: "medium"},
        // @ts-ignore
        {label: t("personalization.generatedImages.quality.high"), value: "high"},
    ];

    const videoQualityOptions = [
        // @ts-ignore
        {label: t("personalization.generatedImages.quality.medium"), value: "Medium"},
        // @ts-ignore
        {label: t("personalization.generatedImages.quality.high"), value: "High"},
    ];

    const imageFormatOptions = [
        {label: "PNG", value: "png"},
        {label: "JPEG", value: "jpeg"},
        {label: "WEBP", value: "webp"}
    ];

    const videoModelOptions = [
        // @ts-ignore
        {label: t("personalization.videoModel.portrait"), value: "Portrait"},
        // @ts-ignore
        {label: t("personalization.videoModel.landscape"), value: "Landscape"},
    ];

    const imagesSizeOptions = [
        // @ts-ignore
        {label: t("personalization.generatedImages.background.auto"), value: "auto"},
        {label: "1024x1024", value: "1024x1024"},
        {label: "1536x1024", value: "1536x1024"},
        {label: "1024x1536", value: "1024x1536"},
    ];

    const videoSizeOptions = [
        // @ts-ignore
        {label: "720x1280", value: "720x1280"},
        {label: "1024x1792", value: "1024x1792"},
    ];

    const videoTimeOptions = [
        {label: "4", value: "4"},
        {label: "8", value: "8"},
        {label: "12", value: "12"}
    ];

    const languageOptions = [
        // @ts-ignore
        {label: t("personalization.default"), value: "Default"},
        {label: "English", value: "English"},
        {label: "Farsi", value: "Farsi"},
        {label: "Arabic", value: "Arabic"},
        {label: "French", value: "French"},
        {label: "Spanish", value: "Spanish"},
        {label: "German", value: "German"},
        {label: "Russian", value: "Russian"},
        {label: "Chinese", value: "Chinese"},
        {label: "Japanese", value: "Japanese"},
        {label: "Hindi", value: "Hindi"},
        {label: "Turkish", value: "Turkish"},
    ];

    const narratorVoiceOptions = [
        {label: "coral", value: "coral"},
        {label: "alloy", value: "alloy"},
        {label: "ash", value: "ash"},
        {label: "ballad", value: "ballad"},
        {label: "echo", value: "echo"},
        {label: "fable", value: "fable"},
        {label: "nova", value: "nova"},
        {label: "onyx", value: "onyx"},
        {label: "sage", value: "sage"},
        {label: "shimmer", value: "shimmer"}
    ];

    const imagesBgOptions = [
        // @ts-ignore
        {label: t("personalization.generatedImages.background.auto"), value: "auto"},
        // @ts-ignore
        {label: t("personalization.generatedImages.background.opaque"), value: "opaque"},
        // @ts-ignore
        {label: t("personalization.generatedImages.background.transparent"), value: "transparent"}
    ];

    useEffect(() => {
        if (data) {
            let images_quality = qualityOptions.find((item) => item.label === data?.generated_images_quality);
            let video_quality = videoQualityOptions.find((item) => item.value === data?.generated_video_quality);
            let images_bg = imagesBgOptions.find((item) => item.value === data?.generated_images_bg);
            let video_model = videoModelOptions.find((item) => item.value === data?.generated_video_model);
            let images_sm = imagesSizeOptions.find((item) => item.value === data?.generated_images_size);
            let video_sm = videoSizeOptions.find((item) => item.value === data?.generated_video_size);
            let video_time = videoTimeOptions.find((item) => item.value == data?.generated_video_time);
            let language = languageOptions.find((item) => item.label === data?.language);
            let narrator_voice = narratorVoiceOptions.find((item) => item.label === data?.narrator_voice);

            reset({
                about: data?.about || "",
                nickname: data?.nickname || "",
                job: data?.job || "",
                interests: data?.interests || "",
                generated_images_quality: images_quality || undefined,
                generated_video_quality: video_quality || undefined,
                generated_images_format: {
                    label: data?.generated_images_format?.toUpperCase(),
                    value: data?.generated_images_format
                },
                language: language || languageOptions[0],
                narrator_voice: narrator_voice || narratorVoiceOptions[0],
                generated_images_size: images_sm || undefined,
                generated_video_size: video_sm || undefined,
                generated_video_time: video_time || undefined,
                generated_images_bg: images_bg || undefined,
                generated_video_model: video_model || undefined,
            });

            setUseHistory(Boolean(data?.use_history));
            setWebSearching(Boolean(data?.web_searching));
            setEnableWebSave(Boolean(data?.allow_public_content));
            const list = data?.instructions ? data?.instructions?.split(",") : [];
            setSelectedOptions(list);
            setHasChanged(false);
        } else {
            reset({language: languageOptions[0]});
        }
    }, [data, reset]);

    const onSubmit: (value: PersonalizationForm) => void = (value) => {
        dispatchPostUserPersonalization({
            about: value.about,
            job: value.job,
            nickname: value.nickname,
            interests: value.interests,
            use_history: useHistory,
            allow_public_content: enableWebSave,
            generated_images_quality: value?.generated_images_quality?.value || "",
            generated_video_quality: value?.generated_video_quality?.value || "",
            generated_images_format: value?.generated_images_format?.value || "",
            generated_images_size: value?.generated_images_size?.value || "",
            generated_video_size: value?.generated_video_size?.value || "",
            generated_images_bg: value?.generated_images_bg?.value || "",
            generated_video_time: value?.generated_video_time?.value || "",
            generated_video_model: value?.generated_video_model?.value || "",
            language: value?.language?.value || "",
            narrator_voice: value?.narrator_voice?.value || "",
            web_searching: useWebSearching,
            instructions: selectedOptions.join(),
            dispatchCloseModal: dispatchCloseModal
        });
    }

    const [showAll, setShowAll] = useState(false);
    const visibleOptions = showAll ? OPTIONS : OPTIONS.slice(0, 4);

    return (
        <Modal
            modalName={ModalNames.PersonalizationModal}
            modalClassName={`rounded-[10px] md:w-[500px] xs:w-[90%] ${(mode === "full" || mode === "general" ) ? "h-[600px]" : "h-auto"}`}
            containerClassName={`overflow-y-auto ${getUserPersonalizationLoading ? "flex items-center" : ""}`}
            ignorePadding
            preventToClose
            showCloseIcon
        >
            <HasLoading LoadingComponent={PuffLoader} loading={getUserPersonalizationLoading}>
                <Form className="px-[40px]" control={control} dir={direction}>
                    <Text elementType="p" fontWeight={500} className="text-lg text-start mt-5 ms-0">
                        profile.personalization
                    </Text>
                    {
                        (mode === "full" || mode === "general") ?
                            <>
                                <Div className="my-2 border-b"/>
                                <Div bg="secondary" className="my-4 py-1.5 px-2.5 rounded-[5px] text-center">
                                    <Text elementType="p">personalization.generalSetting</Text>
                                </Div>
                                <Input
                                    label="personalization.customName"
                                    register={register}
                                    inputClassName="#F9F9F9"
                                    name="nickname"
                                    placeholder="personalization.userName"
                                    className="h-[40px] w-full"
                                    wrapperClassName="mt-[20px]"
                                />
                                <div className="flex items-center mt-[4px]">
                                    <Icon Component={InfoCircle} color="iconColor" size={14}/>
                                    <Text elementType="span" color="iconColor" className="ms-1"
                                          fontSize={11}>personalization.nickNameDesc</Text>
                                </div>
                                <Input
                                    label="personalization.occupation"
                                    register={register}
                                    inputClassName="#F9F9F9"
                                    name="job"
                                    className="h-[40px] w-full"
                                    placeholder="personalization.occupationPlaceholder"
                                    wrapperClassName="mt-[20px]"
                                />
                                <div className="flex items-center mt-[4px]">
                                    <Icon Component={InfoCircle} color="iconColor" size={14}/>
                                    <Text elementType="span" color="iconColor" className="ms-1"
                                          fontSize={11}>personalization.separateWithComma</Text>
                                </div>
                                <div className="flex">
                                    <Text elementType="label" className="text-[13px] mb-[8px] mt-[20px] text-end"
                                          fontWeight={600}>
                                        personalization.aboutYourself
                                    </Text>
                                </div>
                                <textarea
                                    {...register("about")}
                                    ref={(e) => {
                                        register("about").ref(e);
                                        if (textareaRef) {
                                            textareaRef.current = e;
                                        }
                                    }}
                                    onInput={(e) => {
                                        resizeTextareaToContent(textareaRef);
                                        e.currentTarget.dir = getTextDirection(e.currentTarget.value);
                                    }}
                                    className="w-full h-[70px] min-h-[70px] text-[13px] rounded-[5px] leading-[23px] bg-[transparent] border outline-0 focus:outline-none py-1.5 px-2.5 resize-y"
                                    //@ts-ignore
                                    placeholder={t("personalization.aboutYourselfPlaceholder")}
                                    style={{color: theme['text']}}
                                    autoComplete="off"
                                    dir={direction}
                                />
                                <Input
                                    label="personalization.interests"
                                    register={register}
                                    inputClassName="#F9F9F9"
                                    name="interests"
                                    className="h-[40px] w-full"
                                    wrapperClassName="mt-[16px]"
                                    placeholder="personalization.interestsPlaceholder"
                                />
                                <div className="flex items-center mt-[4px]">
                                    <Icon Component={InfoCircle} color="iconColor" size={14}/>
                                    <Text elementType="span" color="iconColor" className="ms-1"
                                          fontSize={11}>personalization.separateWithComma</Text>
                                </div>
                                <div className="flex flex-col md:flex-row mt-[25px]">
                                    <div className="flex-1">
                                        <Text elementType="p"
                                              className="text-sm font-medium text-start">personalization.chatLanguage</Text>
                                        <Text elementType="p" className="text-sm text-start mt-1" fontSize={12}
                                              color="iconColor">personalization.defaultLanguageNote</Text>
                                    </div>
                                    <div className="min-w-[110px] ms-1 text-[12px] text-start mt-3 md:mt-0">
                                        <Select dir={"ltr"} control={control} fontSize={12} iconClassName="mx-[3px]"
                                                iconSize={13} name="language" options={languageOptions}/>
                                    </div>
                                </div>
                                <div className="flex flex-col md:flex-row mt-[25px]">
                                    <div className="flex-1">
                                        <Text elementType="p"
                                              className="text-sm font-medium text-start">personalization.narratorVoice</Text>
                                        <Text elementType="p" className="text-sm text-start mt-1" fontSize={12}
                                              color="iconColor">personalization.narratorVoiceNote</Text>
                                    </div>
                                    <div className="capitalize min-w-[110px] ms-1 text-[12px] text-start mt-3 md:mt-0">
                                        <Select dir={"ltr"} control={control} fontSize={12} iconClassName="mx-[3px]"
                                                iconSize={13} name="narrator_voice" options={narratorVoiceOptions}/>
                                    </div>
                                </div>
                                <Text elementType="h6"
                                      className="text-start mt-[20px]">personalization.customSettings</Text>
                                <div className="flex flex-wrap gap-x-1.5 gap-y-2 mt-2.5 mb-[30px]">
                                    {visibleOptions.map(option => (
                                        <Div
                                            key={option}
                                            bg={selectedOptions.includes(option) ? "tertiary" : "header"}
                                            className="px-3.5 py-1.5 border rounded-[20px] cursor-pointer text-[11px]"
                                            border
                                            onClick={() => toggleOption(option)}
                                        >
                                            <Text elementType="span"
                                                  color={selectedOptions.includes(option) ? themeDark ? "background" : "text" : themeDark ? "text" : "text"}
                                                  translatable={false}>
                                                <Trans color="primary" i18nKey={`personalization.${option}`}/>
                                            </Text>
                                        </Div>
                                    ))}
                                    {!showAll && OPTIONS.length > 4 && (
                                        <Div
                                            key="show-more"
                                            bg="header"
                                            className="px-3 py-1 border rounded-full cursor-pointer text-sm"
                                            border
                                            onClick={() => setShowAll(true)}
                                        >
                                            <Text elementType="span" color="text">
                                                ...
                                            </Text>
                                        </Div>
                                    )}
                                </div>
                            </>
                            : null
                    }

                    {
                        (mode === "full" || mode === "image") ?
                            <>
                                <Div className="mt-6 border-b"/>
                                <Div bg="secondary" className="my-4 py-1.5 px-2.5 rounded-[5px] text-center">
                                    <Text elementType="p">personalization.imageSettings</Text>
                                </Div>
                                <div className="flex flex-col md:flex-row">
                                    <div>
                                        <Text elementType="p"
                                              className="text-sm font-medium text-start">personalization.generatedImageQuality</Text>
                                        <Text elementType="p" className="text-sm text-start mt-1" fontSize={12}
                                              color="iconColor">personalization.highQualityExplanation</Text>
                                    </div>
                                    <div className="min-w-[110px] ms-1 text-[12px] text-start mt-3 md:mt-0">
                                        <Select control={control} fontSize={12} iconClassName="mx-[3px]" iconSize={13}
                                                name="generated_images_quality" options={qualityOptions}/>
                                    </div>
                                </div>
                                <div className="flex flex-col md:flex-row mt-5">
                                    <div className="flex-1">
                                        <Text elementType="p"
                                              className="text-sm font-medium text-start">personalization.generatedImageSize</Text>
                                        <Text elementType="p" className="text-sm text-start mt-1" fontSize={12}
                                              color="iconColor">personalization.imageSizeDescription</Text>
                                    </div>
                                    <div className="min-w-[110px] ms-1 text-[12px] text-start mt-3 md:mt-0">
                                        <Select dir={"ltr"} control={control} fontSize={12} iconClassName="mx-[3px]"
                                                iconSize={13}
                                                name="generated_images_size" options={imagesSizeOptions}/>
                                    </div>
                                </div>
                                <div className="flex flex-col md:flex-row mt-5">
                                    <div className="flex-1">
                                        <Text elementType="p"
                                              className="text-sm font-medium text-start">personalization.imageFormat</Text>
                                        <Text elementType="p" className="text-sm text-start mt-1" fontSize={12}
                                              color="iconColor">personalization.imageFormatDesc</Text>
                                    </div>
                                    <div className="min-w-[110px] ms-1 text-[12px] text-start mt-3 md:mt-0">
                                        <Select dir={"ltr"} control={control} fontSize={12} iconClassName="mx-[3px]"
                                                iconSize={13}
                                                name="generated_images_format" options={imageFormatOptions}/>
                                    </div>
                                </div>
                                <div className="flex flex-col md:flex-row mt-5">
                                    <div className="flex-1">
                                        <Text elementType="p"
                                              className="text-sm font-medium text-start">personalization.backgroundStatus</Text>
                                        <Text elementType="p" className="text-sm text-start mt-1" fontSize={12}
                                              color="iconColor">personalization.backgroundOptionNote</Text>
                                    </div>
                                    <div className="min-w-[110px] ms-1 text-[12px] text-start mt-3 md:mt-0">
                                        <Select control={control} fontSize={12} iconClassName="mx-[3px]" iconSize={13}
                                                name="generated_images_bg" options={imagesBgOptions}/>
                                    </div>
                                </div>
                            </>
                            : null
                    }
                    {
                        (mode === "full" || mode === "video") ?
                            <>
                                <Div className="mt-6 border-b"/>
                                <Div bg="secondary" className="my-4 py-1.5 px-2.5 rounded-[5px] text-center">
                                    <Text elementType="p">personalization.videoSettings</Text>
                                </Div>
                                <div className="flex flex-col md:flex-row">
                                    <div>
                                        <Text elementType="p"
                                              className="text-sm font-medium text-start">personalization.videoQuality</Text>
                                        <Text elementType="p" className="text-sm text-start mt-1" fontSize={12}
                                              color="iconColor">personalization.videoQualityDescription</Text>
                                    </div>
                                    <div className="min-w-[110px] ms-1 text-[12px] text-start mt-3 md:mt-0">
                                        <Select control={control} fontSize={12} iconClassName="mx-[3px]" iconSize={13}
                                                name="generated_video_quality" options={videoQualityOptions}/>
                                    </div>
                                </div>
                                <div className="flex flex-col md:flex-row mt-5">
                                    <div className="flex-1">
                                        <Text elementType="p"
                                              className="text-sm font-medium text-start">personalization.videoSize</Text>
                                        <Text elementType="p" className="text-sm text-start mt-1" fontSize={12}
                                              color="iconColor">personalization.videoSizeDescription</Text>
                                    </div>
                                    <div className="min-w-[110px] ms-1 text-[12px] text-start mt-3 md:mt-0">
                                        <Select dir={"ltr"} control={control} fontSize={12} iconClassName="mx-[3px]"
                                                iconSize={13} name="generated_video_size" options={videoSizeOptions}/>
                                    </div>
                                </div>
                                <div className="flex flex-col md:flex-row mt-5">
                                    <div className="flex-1">
                                        <Text elementType="p"
                                              className="text-sm font-medium text-start">personalization.videoMode</Text>
                                        <Text elementType="p" className="text-sm text-start mt-1" fontSize={12}
                                              color="iconColor">personalization.videoOrientationDescription</Text>
                                    </div>
                                    <div className="min-w-[110px] ms-1 text-[12px] text-start mt-3 md:mt-0">
                                        <Select control={control} fontSize={12} iconClassName="mx-[3px]" iconSize={13}
                                                name="generated_video_model" options={videoModelOptions}/>
                                    </div>
                                </div>
                                <div className="flex flex-col md:flex-row mt-5">
                                    <div className="flex-1">
                                        <Text elementType="p"
                                              className="text-sm font-medium text-start">personalization.videoDuration</Text>
                                        <Text elementType="p" className="text-sm text-start mt-1" fontSize={12}
                                              color="iconColor">personalization.videoDurationDescription</Text>
                                    </div>
                                    <div className="min-w-[110px] ms-1 text-[12px] text-start mt-3 md:mt-0">
                                        <Select dir={"ltr"} control={control} fontSize={12} iconClassName="mx-[3px]"
                                                iconSize={13} name="generated_video_time" options={videoTimeOptions}/>
                                    </div>
                                </div>
                            </>
                            : null
                    }

                    {
                        (mode === "full" || mode === "general") ?
                            <>
                                <Div className="my-6 border-b"/>
                                <ToggleSwitch
                                    boxClassName="mt-4"
                                    label="personalization.webSearchingTitle"
                                    description="personalization.webSearchingDesc"
                                    checked={useWebSearching}
                                    onChange={handleToggleMemory}

                                />
                                <ToggleSwitch
                                    boxClassName="mt-4"
                                    label="personalization.recentChatMemoryTitle"
                                    description="personalization.recentChatMemoryDesc"
                                    checked={useHistory}
                                    onChange={handleToggleHistory}
                                />
                                <ToggleSwitch
                                    boxClassName="mt-4"
                                    label="personalization.enableWebSave"
                                    description="personalization.contentIndexing"
                                    checked={enableWebSave}
                                    onChange={handleToggleEnableWebSave}
                                />
                            </>
                            : null
                    }

                    <Button
                        loading={postUserPersonalizationLoading}
                        disabled={!isDirty && !hasChanged}
                        title="personalization.savePersonalization"
                        bg="primary"
                        onClick={handleSubmit(onSubmit)}
                        className="w-full h-[42px] my-[30px] text-[14px]"
                    />
                </Form>
            </HasLoading>
        </Modal>
    );
};